// Safe Markdown -> HTML for in-app previews. `marked` parses GFM and DOMPurify
// strips unsafe markup before dangerouslySetInnerHTML sees it.
//
// Two entry points:
//   • renderMarkdown    — standard CommonMark/GFM (for real .md files)
//   • renderMessageHtml — mirrors the outgoing-email renderer (single newline
//                         becomes <br>) so the composer preview matches email.
import { marked } from "marked";
import DOMPurify from "dompurify";
import { transformGithubAlerts } from "./markdownAlerts";

// Force links to open in a new tab safely.
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A" && node.getAttribute("href")) {
    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  }
});

function render(md, options) {
  let rawHtml = marked.parse(String(md || ""), { gfm: true, ...options });
  // Rewrite GitHub alert blockquotes before sanitizing.
  rawHtml = transformGithubAlerts(rawHtml);
  // Keep external markdown on the app's CSS, not author-supplied inline styles.
  return DOMPurify.sanitize(rawHtml, {
    USE_PROFILES: { html: true },
    FORBID_ATTR: ["style"],
  });
}

// Standard rendering: blank line = new paragraph, single newline = space.
export function renderMarkdown(md) {
  return render(md, { breaks: false });
}

// True for hrefs that should resolve against the markdown file's directory.
export function isRelativeAssetHref(href) {
  if (!href) return false;
  return !/^([a-z][a-z0-9+.-]*:|#|\/\/|\/)/i.test(href);
}

// Resolve a markdown-relative href into a repo/storage-relative path.
export function resolveRelativeAssetPath(baseDir, href) {
  const baseParts = baseDir ? baseDir.split("/").filter(Boolean) : [];
  for (const part of href.split("/")) {
    if (part === "" || part === ".") continue;
    if (part === "..") baseParts.pop();
    else baseParts.push(part);
  }
  return baseParts.join("/");
}

// Message-composer rendering: single newline = <br>, matching how the email
// body is generated so the admin's preview reflects what recipients receive.
export function renderMessageHtml(md) {
  return render(md, { breaks: true });
}
